// Nomes que o cartão precisa e a lista de descontos não traz: postos, combustíveis, etiquetas e grupos.
// Postos e combustíveis vêm inteiros (poucos e servem de filtro); etiquetas e grupos só os ids da página.
import { useMemo } from 'react';
import { useGetList, useGetMany } from 'react-admin';
import { Lookups, NamedItem } from './discountSummary';

const FIVE_MINUTES = 5 * 60 * 1000;

const toMap = (items: Array<NamedItem | undefined> | undefined) =>
    new Map((items || []).filter((item): item is NamedItem => Boolean(item?.id)).map(item => [String(item.id), String(item.name ?? '')]));

const idsOf = (records: any[] | undefined, key: string): string[] =>
    Array.from(new Set((records || []).flatMap(record => (Array.isArray(record?.[key]) ? record[key] : []))));

export const useDiscountLookups = (records: any[] | undefined, chainId: string | undefined): Lookups => {
    const enabled = Boolean(chainId);
    // Falha de permissão ou rede não derruba a lista: o cartão mostra a contagem no lugar do nome.
    const stationsQuery = useGetList<NamedItem>(
        'stations',
        { filter: { chainId }, pagination: { page: 1, perPage: 1000 }, sort: { field: 'name', order: 'ASC' } },
        { enabled, staleTime: FIVE_MINUTES, retry: false },
    );
    const fuelsQuery = useGetList<NamedItem>(
        'fuels',
        { pagination: { page: 1, perPage: 9999 }, sort: { field: 'name', order: 'ASC' } },
        { enabled, staleTime: FIVE_MINUTES, retry: false },
    );
    const tagIds = useMemo(() => idsOf(records, 'customerTagIds'), [records]);
    const groupIds = useMemo(() => idsOf(records, 'customerGroupIds'), [records]);
    const tagsQuery = useGetMany<NamedItem>('chains/chainId/tag', { ids: tagIds }, { enabled: enabled && tagIds.length > 0, staleTime: FIVE_MINUTES, retry: false });
    const groupsQuery = useGetMany<NamedItem>('chains/chainId/customer-groups', { ids: groupIds }, { enabled: enabled && groupIds.length > 0, staleTime: FIVE_MINUTES, retry: false });

    const stations = stationsQuery.data;
    const fuels = fuelsQuery.data;
    const tags = tagsQuery.data;
    const groups = groupsQuery.data;
    const loading =
        (enabled && stationsQuery.isPending && !stationsQuery.error) ||
        (enabled && fuelsQuery.isPending && !fuelsQuery.error) ||
        (tagIds.length > 0 && tagsQuery.isPending && !tagsQuery.error) ||
        (groupIds.length > 0 && groupsQuery.isPending && !groupsQuery.error);

    return useMemo(
        () => ({ stations: stations || [], fuels: toMap(fuels), tags: toMap(tags), groups: toMap(groups), loading: Boolean(loading) }),
        [stations, fuels, tags, groups, loading],
    );
};
